import { FC, useEffect, useMemo } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useUsers } from '../../contexts/users.context';
import { ParentUser, ParentRelation, UserStatus } from '../../users.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { useCreateParentUser } from '../../hooks/useParentHooks';

type ParentFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  userName: string;
  userPassword: string;
  relation: ParentRelation;
  occupation?: string;
  workPhone?: string;
  emergencyContact: boolean;
  status: UserStatus;
  notes?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
};

const getParentFormSchema = (translateText: (key: string) => string) =>
  z.object({
    firstName: z
      .string()
      .min(2, translateText('common.fieldRequired'))
      .max(50, translateText('users.forms.validation.firstNameMax')),
    lastName: z
      .string()
      .min(2, translateText('common.fieldRequired'))
      .max(50, translateText('users.forms.validation.lastNameMax')),
    email: z
      .string()
      .email(translateText('users.forms.validation.emailInvalidFormat'))
      .min(1, translateText('common.fieldRequired')),
    phone: z
      .string()
      .min(10, translateText('users.forms.validation.phoneMinDigits'))
      .max(15, translateText('users.forms.validation.phoneMaxDigits')),
    countryCode: z.string().min(1, translateText('common.fieldRequired')),
    userName: z.string().min(3, translateText('users.forms.validation.userNameMin')),
    userPassword: z.string().min(6, translateText('users.forms.validation.userPasswordMin')),
    relation: z.nativeEnum(ParentRelation, {
      required_error: translateText('common.fieldRequired'),
    }),
    occupation: z.string().optional(),
    workPhone: z.string().optional(),
    emergencyContact: z.boolean(),
    status: z.nativeEnum(UserStatus),
    notes: z.string().optional(),
    address: z.object({
      street: z.string().min(1, translateText('users.forms.validation.streetRequired')),
      city: z.string().min(1, translateText('users.forms.validation.cityRequired')),
      state: z.string().min(1, translateText('users.forms.validation.stateRequired')),
      zipCode: z.string().min(1, translateText('users.forms.validation.zipCodeRequired')),
      country: z.string().min(1, translateText('users.forms.validation.countryRequired')),
    }),
  });

const getRelationOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: ParentRelation.FATHER,
      value: translateText('users.options.relation.father'),
    },
    {
      key: ParentRelation.MOTHER,
      value: translateText('users.options.relation.mother'),
    },
    {
      key: ParentRelation.GUARDIAN,
      value: translateText('users.options.relation.guardian'),
    },
    {
      key: ParentRelation.STEP_FATHER,
      value: translateText('users.options.relation.stepFather'),
    },
    {
      key: ParentRelation.STEP_MOTHER,
      value: translateText('users.options.relation.stepMother'),
    },
    {
      key: ParentRelation.GRANDPARENT,
      value: translateText('users.options.relation.grandparent'),
    },
    {
      key: ParentRelation.OTHER,
      value: translateText('users.options.relation.other'),
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: UserStatus.ACTIVE,
      value: translateText('users.options.status.active'),
    },
    {
      key: UserStatus.INACTIVE,
      value: translateText('users.options.status.inactive'),
    },
    {
      key: UserStatus.PENDING,
      value: translateText('users.options.status.pending'),
    },
    {
      key: UserStatus.BLOCKED,
      value: translateText('users.options.status.blocked'),
    },
  ];

const AddEditParentUserView: FC = () => {
  const { translateText } = useTranslation();
  const { state, closeAddEditParent, fetchParentUsers } = useUsers();
  const {
    isAddEditParentOpen,
    isCreateMode,
    isEditMode,
    selectedUser,
    isLoading,
  } = state;

  const createMutation = useCreateParentUser();

  const parentFormSchema = useMemo(
    () => getParentFormSchema(translateText),
    [translateText]
  );
  const relationOptions = useMemo(
    () => getRelationOptions(translateText),
    [translateText]
  );
  const statusOptions = useMemo(
    () => getStatusOptions(translateText),
    [translateText]
  );

  const defaultValues: ParentFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+91',
    userName: '',
    userPassword: '',
    relation: ParentRelation.FATHER,
    occupation: '',
    workPhone: '',
    emergencyContact: false,
    status: UserStatus.PENDING,
    notes: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  };

  const methods = useForm<ParentFormData>({
    resolver: zodResolver(parentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (isAddEditParentOpen && selectedUser && isEditMode) {
      const parentUser = selectedUser as ParentUser;
      reset({
        firstName: parentUser.firstName || '',
        lastName: parentUser.lastName || '',
        email: parentUser.email || '',
        phone: parentUser.phone || '',
        countryCode: '+91',
        userName: '',
        userPassword: '',
        relation: parentUser.relation || ParentRelation.FATHER,
        occupation: parentUser.occupation || '',
        workPhone: parentUser.workPhone || '',
        emergencyContact: parentUser.emergencyContact || false,
        status: parentUser.status || UserStatus.PENDING,
        notes: parentUser.notes || '',
        address: parentUser.address || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        },
      });
    } else if (isAddEditParentOpen && isCreateMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddEditParentOpen, selectedUser, isEditMode, isCreateMode, reset]);

  const onSubmitForm = async (data: ParentFormData) => {
    if (!isCreateMode) {
      // TODO: Implement update
      closeAddEditParent();
      return;
    }

    try {
      await createMutation.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        countryCode: data.countryCode,
        userName: data.userName,
        userPassword: data.userPassword,
        relation: data.relation,
        occupation: data.occupation,
        workPhone: data.workPhone,
        emergencyContact: data.emergencyContact,
        children: [],
        communicationPreferences: { email: true, sms: false, phone: false, push: false },
        address: data.address,
        notes: data.notes,
        userCategory: 'guardian',
      });
      await fetchParentUsers();
      closeAddEditParent();
    } catch (error) {
      console.error('Error creating parent user:', error);
    }
  };

  const title = isEditMode
    ? translateText('users.forms.editParent')
    : translateText('users.forms.addParent');

  return (
    <StructuredModal
      isOpen={isAddEditParentOpen}
      onClose={closeAddEditParent}
      title={title}
      drawerWidth="60vw"
      data-testid="add-edit-parent-user-modal"
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={
        isEditMode
          ? translateText('common.update')
          : translateText('common.save')
      }
      footerNegativeAction={closeAddEditParent}
      footerNegativeActionText={translateText('common.cancel')}
      isLoading={isLoading || createMutation.isPending}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmitForm)}
          style={{ width: '100%' }}
          data-testid="parent-user-form"
        >
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ p: { xs: 1, sm: 2 } }}
            data-testid="parent-form-container"
          >
            <Grid item xs={12} data-testid="basic-details-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.basicInformation')}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="first-name-field">
              <ControlledTextField
                control={control}
                name="firstName"
                id="first-name"
                label={translateText('users.forms.fields.firstName')}
                errorMessage={errors.firstName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="last-name-field">
              <ControlledTextField
                control={control}
                name="lastName"
                id="last-name"
                label={translateText('users.forms.fields.lastName')}
                errorMessage={errors.lastName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="email-field">
              <ControlledTextField
                control={control}
                name="email"
                id="email"
                label={translateText('users.forms.fields.email')}
                errorMessage={errors.email?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="phone-field">
              <ControlledTextField
                control={control}
                name="phone"
                id="phone"
                label={translateText('users.forms.fields.phone')}
                errorMessage={errors.phone?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="relation-field">
              <ControlledSelectField
                control={control}
                name="relation"
                id="relation"
                label={translateText('users.forms.fields.relation')}
                itemList={relationOptions}
                errorMessage={errors.relation?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="status-field">
              <ControlledSelectField
                control={control}
                name="status"
                id="status"
                label={translateText('common.status')}
                itemList={statusOptions}
                errorMessage={errors.status?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="occupation-field">
              <ControlledTextField
                control={control}
                name="occupation"
                id="occupation"
                label={translateText('users.forms.fields.occupation')}
                errorMessage={errors.occupation?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="work-phone-field">
              <ControlledTextField
                control={control}
                name="workPhone"
                id="work-phone"
                label={translateText('users.forms.fields.workPhone')}
                errorMessage={errors.workPhone?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="credentials-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.loginCredentials')}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="username-field">
              <ControlledTextField
                control={control}
                name="userName"
                id="user-name"
                label={translateText('users.forms.fields.userName')}
                errorMessage={errors.userName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="password-field">
              <ControlledTextField
                control={control}
                name="userPassword"
                id="user-password"
                label={translateText('users.forms.fields.userPassword')}
                type="password"
                errorMessage={errors.userPassword?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="address-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.addressDetails')}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="address-street-field">
              <ControlledTextField
                control={control}
                name="address.street"
                id="address-street"
                label={translateText('users.forms.fields.street')}
                errorMessage={errors.address?.street?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="address-city-field">
              <ControlledTextField
                control={control}
                name="address.city"
                id="address-city"
                label={translateText('users.forms.fields.city')}
                errorMessage={errors.address?.city?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-state-field">
              <ControlledTextField
                control={control}
                name="address.state"
                id="address-state"
                label={translateText('users.forms.fields.state')}
                errorMessage={errors.address?.state?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-zipcode-field">
              <ControlledTextField
                control={control}
                name="address.zipCode"
                id="address-zipcode"
                label={translateText('users.forms.fields.zipCode')}
                errorMessage={errors.address?.zipCode?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-country-field">
              <ControlledTextField
                control={control}
                name="address.country"
                id="address-country"
                label={translateText('users.forms.fields.country')}
                errorMessage={errors.address?.country?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="notes-field">
              <ControlledTextField
                control={control}
                name="notes"
                id="notes"
                label={translateText('common.remarks')}
                errorMessage={errors.notes?.message}
                multiline
                rows={3}
                isGridItem={false}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default AddEditParentUserView;
